import React, { useCallback, useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { CommonTableV2 } from '../../components/common/Table/CommonTable-v2';
import {
  type Invoice,
  useInvoiceQuery,
  useInvoiceGasBreakdownQuery,
} from '../../lib/queries/invoiceQuery';
import { format } from 'date-fns';
import { PrimaryButton } from '../../components/common/Button/Buttons';
import { utils, writeFileXLSX } from 'xlsx';
import { Modal } from '../../components/common/Modal/Modal';
import { toast } from 'react-toastify';
import { useInvoicePaymentEventsMutation } from '../../lib/queries/invoicePaymentEventMutation';
import { eurCentsToEur } from '@tayttopaikka/pricing';
import { InvoiceStats } from '../../components/Invoice/InvoiceStats';

type InvoiceTableRow = {
  name: string | null;
  email: string | null;
  date: string | null;
  gasMixture: string | null;
  description: string | null;
  priceEurCents: number;
  subRows?: InvoiceTableRow[];
};

const columnHelper = createColumnHelper<InvoiceTableRow>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Nimi',
    cell: (info) => info.getValue() ?? '',
  }),
  columnHelper.accessor('email', {
    header: 'Yhteystiedot',
    cell: (info) => info.getValue() ?? '',
  }),
  columnHelper.accessor('date', {
    header: 'Päivämäärä',
    cell: (info) => info.getValue() ?? '',
  }),
  columnHelper.accessor('gasMixture', {
    header: 'Kaasuseos',
    cell: (info) => info.getValue() ?? '',
  }),
  columnHelper.accessor('description', {
    header: 'Kuvaus',
    cell: (info) => info.getValue() ?? '',
  }),
  columnHelper.accessor('priceEurCents', {
    header: 'Hinta (€)',
    cell: (info) => `${eurCentsToEur(info.getValue())} €`,
  }),
];

export const InvoicePage: React.FC = () => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [exportedData, setExportedData] = useState<Invoice[]>([]);
  const { data } = useInvoiceQuery();
  const { data: gasBreakdown } = useInvoiceGasBreakdownQuery();

  const onMutationSuccess = useCallback(() => {
    setIsConfirmModalOpen(false);
    setExportedData([]);
    toast.success('Laskut merkitty laskutetuiksi.');
  }, []);

  const { mutate: createInvoicePaymentEvents } =
    useInvoicePaymentEventsMutation(onMutationSuccess);

  const tableData = useMemo(
    () =>
      data
        ?.slice()
        .sort((a, b) =>
          `${a.user.surname} ${a.user.forename}`.localeCompare(
            `${b.user.surname} ${b.user.forename}`,
          ),
        )
        .map((invoice) => ({
          name: `${invoice.user.surname}, ${invoice.user.forename}`,
          email: invoice.user.email,
          date: null,
          gasMixture: null,
          description: null,
          priceEurCents: invoice.invoiceTotal,
          subRows: invoice.invoiceRows.map((row) => ({
            name: null,
            email: null,
            date: format(new Date(row.date), 'dd.MM.yyyy HH:mm'),
            gasMixture: row.gasMixture,
            description: row.description,
            priceEurCents: row.price,
          })),
        })) ?? [],
    [data],
  );

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table v8 is not React Compiler-compatible; memoization is handled manually above
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.subRows,
    initialState: { expanded: true },
  });

  const onExportInvoicesButtonClick = useCallback(() => {
    if (data) {
      // generate worksheet from state
      const ws = utils.json_to_sheet(
        data
          .slice()
          .sort((a, b) =>
            `${a.user.surname} ${a.user.forename}`.localeCompare(
              `${b.user.surname} ${b.user.forename}`,
            ),
          )
          .map((invoice) => ({
            Nimi: `${invoice.user.surname} ${invoice.user.forename}`,
            Yhteystiedot: invoice.user.email,
            'Hinta (€)': eurCentsToEur(invoice.invoiceTotal),
            Tapahtumat: invoice.invoiceRows
              .map(
                (row) =>
                  `${format(new Date(row.date), 'dd.MM.yyyy HH:mm')} ${row.gasMixture} ${row.description} ${eurCentsToEur(row.price)} €`,
              )
              .join(', '),
          })),
      );

      // create workbook and append worksheet
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Data');

      // export to XLSX
      writeFileXLSX(
        wb,
        `tayttopaikka_laskut_${new Date().toISOString().split('T')[0]}.xlsx`,
      );

      toast.info(
        'Laskut viety onnistuneesti Excel-tiedostoon. Voit nyt merkitä ladatut laskut laskutetuiksi.',
        {
          autoClose: 30000,
          position: 'top-left',
        },
      );
      setExportedData(data);
    } else {
      toast.error(
        'Odottamaton virhe. Lataa sivu uudestaan ja yritä uudelleen.',
      );
    }
  }, [data]);

  const onConfirmModalOpen = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const onConfirmModalClose = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const onConfirmModalConfirm = useCallback(() => {
    createInvoicePaymentEvents(exportedData);
  }, [createInvoicePaymentEvents, exportedData]);

  return (
    <div>
      <Modal
        onClose={onConfirmModalClose}
        onConfirm={onConfirmModalConfirm}
        isOpen={isConfirmModalOpen}
        title="Merkitse tapahtumat laskutetuiksi"
        confirmButtonText="Merkitse laskutetuiksi"
      >
        <p>
          Tarkista ladattu Excel-tiedosto. Mikäli tiedosto on kunnossa, merkitse
          laskut laskutetuiksi.
        </p>
      </Modal>
      <h1>Laskutus</h1>
      <div>
        <p>
          Alle on listattu kaikki Täyttöpaikan käyttäjien tekemät{' '}
          <b>laskuttamattomat</b> täytöt. Voit viedä laskut Excel-tiedostoon
          painamalla oikeassa yläkulmassa olevaa "Vie laskut" -nappia.
        </p>
        <p>
          Kun laskut on viety Excel-tiedostoon, ne voidaan merkitä
          laskutetuiksi. Merkkaaminen on käytettävissä vain, kun laskut on viety
          onnistuneesti. Järjestelmä ei sinänsä mene rikki vaikka laskuja ei
          merkattaisikaan laskutetuiksi, mutta mikäli käyttäjille lähetetään
          laskut ladatun tiedoston perusteella, mutta laskuja ei merkata
          laskutetuksi, voi käyttäjälle pahimmillaan lähteä useampi lasku
          samasta täyttötapahtumasta.
        </p>
        <p>
          Kun "Merkitse laskutetuiksi" -nappia painetaan, käyttäjille ilmestyy
          laskun luomisesta tieto. On suositeltavaa, että itse laskujen lähetys
          tapahtuu mahdollisimman pian merkkauksen jälkeen, jotta käyttäjät
          eivät jää ihmettelemään saapumatonta laskua.
        </p>
        <p>
          Laskutettujen täyttöjen tiedot tallennetaan järjestelmään eikä mitään
          tietoa katoa. Järjestelmä ei kuitenkaan tällä hetkellä tue laskujen
          uudelleen esille ottamista.
        </p>
        <p>
          Hätätilanteessa järjestelmän ylläpitäjät saavat taiottua laskut
          uudelleen esille. Jos tarvitset apua, ota yhteyttä ylläpitoon tai
          lähetä sähköpostia{' '}
          <a href="mailto:info@tayttopaikka.fi">info@tayttopaikka.fi</a>.
        </p>
      </div>
      <InvoiceStats invoices={data ?? []} gasBreakdown={gasBreakdown ?? []} />
      <div>
        <div className="d-flex flex-row justify-content-between pb-2">
          <h2>Laskuttamattomat täyttötapahtumat</h2>
          <div className="d-flex" style={{ gap: '8px' }}>
            <PrimaryButton
              text="Merkitse laskutetuiksi"
              onClick={onConfirmModalOpen}
              disabled={exportedData.length === 0}
            />
            <PrimaryButton
              disabled={data?.length === 0}
              text="Vie laskut"
              onClick={onExportInvoicesButtonClick}
            />
          </div>
        </div>
        <CommonTableV2 table={table} />
      </div>
    </div>
  );
};
